import * as motion from "motion/react-m";
import { MotionProps, Variants } from "motion/react";
import React, { ReactNode, useEffect, useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  fade_in_left_variant,
  fade_in_right_variant,
} from "../../utils/animation/transitions";
// import { RowDirectionType } from "./types/LayoutType";

export const FadeInFromBottom = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <motion.span
      className="button-fade-in-bottom"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.3 }}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: 50 },
      }}
    >
      {children}
    </motion.span>
  );
};

export const FadeInImage = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      className="animation-wrapper"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      variants={{
        visible: { opacity: 1 },
        hidden: { opacity: 0.3 },
      }}
    >
      {children}
    </motion.div>
  );
};

export const FadeInFromLeft = ({ children }: { children: React.ReactNode }) => {
  const [variantsLeft, setVariantsLeft] = useState({
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
    },
    hidden: {
      opacity: 0,
      x: -200, //TODO: change animation from mobile to Desktop, now not working useEffect on Desktop.
      y: 0, // Real values: x: 0, y: 200
    },
  });
  const isMobile = useMediaQuery("(max-width: 767px)");
  useEffect(() => {
    if (!isMobile) {
      setVariantsLeft({
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
        },
        hidden: {
          opacity: 0,
          x: -200,
          y: 0,
        },
      });
    }
  }, [isMobile]);

  return (
    <motion.div
      className="animation-wrapper w-full h-full"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      transition={{ ease: "easeOut", duration: 0.3 }}
      variants={variantsLeft}
    >
      {children}
    </motion.div>
  );
};

export const FadeInFromRight = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isTouch = useMediaQuery("(max-width: 1023px)");
  const variantsRight = isTouch
    ? {
        visible: {
          opacity: 1,
          y: 0,
          x: 0,
        },
        hidden: {
          opacity: 0,
          x: 0,
          y: 200,
        },
      }
    : {
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
        },
        hidden: {
          opacity: 0,
          y: 0,
          x: 200,
        },
      };
  return (
    <motion.div
      className="animation-wrapper h-full w-full"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      transition={{ ease: "easeOut", duration: 0.3 }}
      variants={variantsRight}
    >
      {children}
    </motion.div>
  );
};

export const FadeInWithPulse = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <motion.span
      className="animation-wrapper"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.7 }}
      variants={{
        visible: { opacity: [1, 1, 1], scale: [1, 1.2, 1], y: [0, 0, 0] },
        hidden: { opacity: 0, scale: 1, y: 100 },
      }}
    >
      {children}
    </motion.span>
  );
};
interface FadeAnimationsProp {
  children: React.ReactNode;
  animationType: "fade_from_left" | "fade_from_right";
}

export const FadeAnimations = ({
  children,
  animationType,
}: FadeAnimationsProp) => {
  let animtionJSX = <></>;
  switch (animationType) {
    case "fade_from_left":
      animtionJSX = <FadeInFromLeft>{children}</FadeInFromLeft>;
      break;
    case "fade_from_right":
      animtionJSX = <FadeInFromRight>{children}</FadeInFromRight>;
      break;
    default:
      animtionJSX = <FadeInFromLeft>{children}</FadeInFromLeft>;
      break;
  }

  return animtionJSX;
};

type MotionWrapperProps = MotionProps & {
  children: React.ReactNode;
  variants?: Variants;
};

export const MotionWrapper: React.FC<MotionWrapperProps> = ({
  children,
  variants,
  ...motionProps
}) => {
  const childrenArray = React.Children.toArray(children);

  if (childrenArray.length === 0) {
    return null;
  }

  const firstChild = childrenArray[0];

  if (!React.isValidElement(firstChild)) {
    return null;
  }

  const isValidMotionElement =
    typeof firstChild.type === "string" && firstChild.type in motion;

  if (!isValidMotionElement) {
    return (
      <motion.div
        variants={variants}
        initial={motionProps.initial}
        whileInView={motionProps.whileInView}
        viewport={motionProps.viewport}
        className="h-100 w-100"
      >
        {children}
      </motion.div>
    );
  }

  const MotionElement = motion[
    firstChild.type as keyof typeof motion
  ] as React.ElementType;

  const newProps = {
    ...firstChild.props,
    variants,
    ...motionProps,
  };

  const newChildren = [
    React.createElement(MotionElement, newProps, firstChild.props.children),
    ...childrenArray.slice(1),
  ];

  return <>{newChildren}</>;
};

interface AnimatedContentProps {
  direction: string;
  children: ReactNode;
}

export const AnimatedContent = ({
  direction,
  children,
}: AnimatedContentProps) => {
  return direction === "is-row-reverse-desktop" ? (
    <MotionWrapper
      variants={fade_in_right_variant}
      initial="initial"
      whileInView={"animate"}
      viewport={{ once: true }}
    >
      {children}
    </MotionWrapper>
  ) : (
    <MotionWrapper
      variants={fade_in_left_variant}
      initial="initial"
      whileInView={"animate"}
      viewport={{ once: true }}
    >
      {children}
    </MotionWrapper>
  );
};
